import { useEffect, useMemo, useRef } from "react";

import SettingsIcon from "@mui/icons-material/Settings";
import { Box, Typography } from "@mui/material";
import type { Conversation } from "@rubi/api-generated/clientRs";
import { useTranslation } from "react-i18next";

import Chatline from "./Chatline";

const formatPhoneNumber = (phone: string) => {
  const cleaned = phone.replace(/\D/g, "");
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  return phone;
};

interface ChatConversationProps {
  conversation: Conversation;
  onConversationClosed?: () => void;
}

const ChatConversation = ({
  conversation,
  onConversationClosed,
}: ChatConversationProps) => {
  const { t } = useTranslation("message");
  const previousTimeClosedRef = useRef<string | null | undefined>(
    conversation.time_closed,
  );

  const timeClosed = conversation.time_closed;

  // Detect when conversation becomes closed and notify parent to scroll
  useEffect(() => {
    const wasOpen = !previousTimeClosedRef.current;
    const isNowClosed = !!timeClosed;

    if (wasOpen && isNowClosed && onConversationClosed) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          onConversationClosed();
        });
      });
    }

    previousTimeClosedRef.current = timeClosed;
  }, [timeClosed, onConversationClosed]);

  const allMessages = useMemo(() => {
    return [
      ...(conversation.outbound_messages || []).map((msg) => ({
        ...msg,
        type: "outbound" as const,
        timestamp: msg.time_sent,
      })),
      ...(conversation.inbound_messages || []).map((msg) => ({
        ...msg,
        type: "inbound" as const,
        timestamp: msg.time_received,
      })),
    ].sort(
      (a, b) =>
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
    );
  }, [conversation.outbound_messages, conversation.inbound_messages]);

  const isSameDay = (date1: string, date2: string) => {
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    return (
      d1.getFullYear() === d2.getFullYear() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getDate() === d2.getDate()
    );
  };

  const formatDate = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const displayName = conversation.first_name
    ? `${conversation.first_name}${conversation.last_name ? ` ${conversation.last_name}` : ""}`
    : formatPhoneNumber(conversation.sms_phone_number);

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const isToday = (timestamp: string) => {
    const date = new Date(timestamp);
    const today = new Date();
    return (
      date.getFullYear() === today.getFullYear() &&
      date.getMonth() === today.getMonth() &&
      date.getDate() === today.getDate()
    );
  };

  const formatClosedTime = (timestamp: string) => {
    if (isToday(timestamp)) {
      return formatTime(timestamp);
    }
    const date = new Date(timestamp);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  return (
    <>
      {allMessages.map((message, idx) => {
        const showDateSeparator =
          idx === 0 ||
          !isSameDay(message.timestamp, allMessages[idx - 1].timestamp);

        // Get sender name for outbound messages
        let senderName: string | undefined;
        if (message.type === "outbound") {
          if (message.sender) {
            senderName = `${message.sender.first_name} ${message.sender.last_name}`;
          } else {
            const participant = (conversation.participants || []).find(
              (p) => p.user?.id === message.sender_id,
            );
            if (participant?.user) {
              senderName = `${participant.user.first_name} ${participant.user.last_name}`;
            }
          }
        } else {
          if (conversation.first_name) {
            senderName = `${conversation.first_name}${conversation.last_name ? ` ${conversation.last_name}` : ""}`;
          }
        }

        const messageKey =
          `message_sid` in message && message.message_sid
            ? `${message.type}-${message.message_sid}`
            : `${message.type}-${idx}`;

        return (
          <Chatline
            key={messageKey}
            message={message}
            displayName={displayName}
            senderName={senderName}
            senderInitial={conversation.first_name?.[0] || "U"}
            showDateSeparator={showDateSeparator}
            dateLabel={formatDate(message.timestamp)}
            orgId={conversation.rubi_org_id}
          />
        );
      })}

      {timeClosed && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "left",
            my: 2,
          }}
        >
          <SettingsIcon sx={{ color: "text.disabled" }} />
          <Typography
            variant="body2"
            sx={{
              color: "text.disabled",
              fontSize: "0.875rem",
              marginLeft: 1,
              marginTop: 0.25,
            }}
          >
            {t("conversation.closedInactive", {
              time: formatClosedTime(timeClosed),
            })}
          </Typography>
        </Box>
      )}
    </>
  );
};

export default ChatConversation;
